"use client";

import { cn } from "@/lib/utils";
import {
  AnimatePresence,
  motion,
  useScroll,
  useSpring,
  useMotionValueEvent,
} from "framer-motion";
import { useState } from "react";

export default function ScrollToTop() {
  const [isVisible, setIsVisible] = useState(false);

  // 1. Ambil nilai scroll mentah dari Framer Motion
  const { scrollY, scrollYProgress } = useScroll();

  // 2. Terapkan efek Spring pada progress scroll (0 - 1)
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  // 3. Logic Visibility menggunakan useMotionValueEvent (Pengganti useEffect manual)
  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest > 300) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  });

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.5, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.5, y: 20 }}
          whileHover={{ scale: 1.1, y: -5 }}
          whileTap={{ scale: 0.9 }}
          onClick={scrollToTop}
          className={cn(
            "fixed bottom-8 right-8 z-50 flex h-14 w-14 items-center justify-center rounded-full",
            "bg-background/80 backdrop-blur-md shadow-2xl",
            "group transition-all duration-300",
            "border border-slate-200 dark:border-slate-800",
          )}
          aria-label="Scroll to top"
        >
          {/* 1. PROGRESS RING SVG */}
          <svg
            className="absolute inset-0 h-full w-full -rotate-90 transform"
            viewBox="0 0 48 48"
          >
            {/* Track Ring (Background Circle) */}
            <circle
              className="text-slate-200 dark:text-slate-800"
              strokeWidth="3"
              stroke="currentColor"
              fill="transparent"
              r="20"
              cx="24"
              cy="24"
            />
            {/* Progress Indicator (Filled Circle) - Diubah menjadi motion.circle */}
            <motion.circle
              className="text-primary dark:text-purple-500"
              strokeWidth="3"
              strokeLinecap="round"
              stroke="currentColor"
              fill="transparent"
              r="20"
              cx="24"
              cy="24"
              // CUKUP GUNAKAN PATHLENGTH! Framer Motion akan mengurus perhitungan dashoffset otomatis
              style={{ pathLength: smoothProgress }}
            />
          </svg>

          {/* 2. CUSTOM FUTURISTIC ARROW ICON */}
          <div className="relative z-10 flex flex-col items-center justify-center">
            <motion.svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-slate-700 dark:text-white"
              variants={{
                hover: { y: -3 },
                initial: { y: 0 },
              }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              {/* Bagian Ekor Panah (Techy Look) */}
              <path
                d="M12 20V14"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              {/* Bagian Kepala Panah (Double Chevron Style) */}
              <path
                d="M5 11L12 4L19 11"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Aksen Kecil di bawah (Thruster) */}
              <motion.path
                d="M10 22H14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                initial={{ opacity: 0, scale: 0 }}
                whileHover={{ opacity: 1, scale: 1 }}
              />
            </motion.svg>
          </div>

          {/* 3. GLOW EFFECT (Behind) */}
          <div className="absolute inset-0 -z-10 rounded-full bg-blue-500/10 dark:bg-purple-500/20 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        </motion.button>
      )}
    </AnimatePresence>
  );
}
